import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchCompanyOverviewTableData } from 'services/verticalsTablesApi'
import { BaseEntityForTable } from 'utils/types'
import { MRT_SortingState } from 'material-react-table'
import { Filter } from 'components/FilterDrawer/FilterDrawer.config'

interface UseNexusMainTableQuery {
  verticalIndices: string
  currentQueryKey: string
  searchValue?: string
  sorting?: MRT_SortingState
  filters?: Filter[]
  pageSize?: number
}

export const useNexusMainTableQuery = <T extends BaseEntityForTable>({
  verticalIndices,
  currentQueryKey,
  searchValue,
  sorting,
  filters,
  pageSize = 50,
}: UseNexusMainTableQuery) => {
  const {
    data: verticalEntities,
    fetchNextPage,
    isFetching,
    isLoading,
    isError,
  } = useInfiniteQuery<T>({
    queryKey: [currentQueryKey, verticalIndices, searchValue, sorting, filters, pageSize],
    queryFn: async ({ pageParam = 0, signal }) => {
      const fromIndex = pageParam * pageSize
      return await fetchCompanyOverviewTableData(
        verticalIndices,
        fromIndex,
        pageSize,
        searchValue,
        sorting,
        filters,
        signal,
      )
    },
    getNextPageParam: (_lastGroup, groups) => groups.length,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const flatEntities = verticalEntities?.pages.flatMap((page: T) => page) || null
  const flatVerticalEntities = flatEntities?.[0] ? flatEntities : null

  return {
    verticalEntities: flatVerticalEntities,
    fetchNextPage,
    isFetching,
    isLoading,
    searchValue,
    isError,
  }
}
